import { useQuill } from "react-quilljs";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import { useEffect } from "react";

const Delta = Quill.import("delta");

// Word/Google Docs/Zoho Writer copy-paste rarely carries real <h1>/<h2> tags -
// headings show up as short, fully-bold plain paragraphs. Detect that shape
// and promote it to a real Quill heading so pasted policy/legal text keeps structure.
const isHeadingLikeParagraph = (node) => {
  if (node.tagName !== "P") return false;

  const text = node.textContent.trim();
  if (!text || text.split(/\s+/).length > 10) return false;

  const bold = node.querySelector("b, strong");
  return !!bold && bold.textContent.trim() === text;
};

export default function RichTextarea({ value, onChange, placeholder = "Write content..." }) {
  const { quill, quillRef } = useQuill({
    theme: "snow",
    placeholder,
    modules: {
      toolbar: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        ["blockquote", "code-block"],
        ["link", "image"],
        ["clean"],
      ],
    },
  });

  useEffect(() => {
    if (!quill) return;

    quill.clipboard.addMatcher("P", (node, delta) => {
      if (!isHeadingLikeParagraph(node)) return delta;
      return delta.compose(new Delta().retain(delta.length(), { header: 2 }));
    });
  }, [quill]);

  useEffect(() => {
    if (quill && value !== undefined) {
      const current = quill.root.innerHTML;
      if (current !== value) {
        quill.root.innerHTML = value || "";
      }
    }
  }, [quill, value]);

  useEffect(() => {
    if (!quill) return;

    const handler = () => {
      const html = quill.root.innerHTML;
      onChange?.(html);
    };

    quill.on("text-change", handler);
    return () => quill.off("text-change", handler);
  }, [quill, onChange]);

  return (
    <div className="w-full max-w-full border rounded-2xl overflow-hidden bg-white">
      <div ref={quillRef} className="w-full" style={{ minHeight: 250 }} />
    </div>
  );
}
